// The thumb joysticks on the running robot's board: one pad each, dragged with
// a pointer, plus the switch under the cap as a held button.
//
// A KY-023 is a sprung gimbal, so the pad is too: dragging holds the stick, and
// letting go returns it to the mechanical centre, as the springs do. Nothing
// latches. That the centre it comes back to is not the *electrical* centre is
// the part's business and not the pad's -- the knob returns to the middle and
// the wipers do not, which is exactly why a sketch reading one has to learn its
// centre first.
//
// The state goes into the controller whole (`set_joystick_at`), rather than as
// whatever changed: a pointer on a pad knows where the stick is and whether the
// cap is down, all at once, and turning that into voltages on two pins and a
// level on a third is the controller's half.
//
// The hardware warning is not here. It is the board's rather than any one
// part's -- a wiper on a pin the sketch drives is the same fault as a switch
// leg on one -- so `switches.ts` polls it for both of us and writes it into the
// panel below this one.

import { AddedRobot, InterfaceHybrid } from "simulator";

// The knob's radius in CSS pixels, which is `.joystick-knob`'s width halved.
// The knob's centre travels the pad's radius less this, so at full throw the
// knob sits inside the rim rather than half over it.
const KNOB_RADIUS = 13;

let hybrid: InterfaceHybrid | undefined;
// Which articulated's board these belong to -- the most recently added robot,
// as everywhere else on the page.
let robot = 0;
// Where each stick is being held and whether its cap is down, in the order
// `set_joystick_at` numbers them. Kept here because it is what the controller
// is told, and the two halves of it are set by different pointers.
let sticks: { x: number; y: number; pressed: boolean }[] = [];

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Squeeze a position into the gate the gimbal actually has, which is round: the
// stick cannot be at full throw on both axes, and pushed into the corner it
// stops on the rim. The part does this too -- what it is for here is the knob,
// which has to be drawn where the stick would be rather than where the pointer
// went.
function gate(x: number, y: number): [number, number] {
  const radius = Math.hypot(x, y);
  return radius <= 1 ? [x, y] : [x / radius, y / radius];
}

// Show the joystick panel for the robot just added, hiding it for a robot that
// has none.
export function useJoysticksOf(scene: InterfaceHybrid, added: AddedRobot) {
  // Let go of anything held on the board being replaced, for the reason the
  // switches do it: the deflection is a state the controller keeps, so a stick
  // abandoned mid-drag would stay pushed over for as long as that robot is in
  // the scene.
  releaseAll();

  hybrid = scene;
  robot = added.index;

  const names = added.joystickNames();
  const wiring = added.joystickWiring();
  sticks = names.map(() => ({ x: 0, y: 0, pressed: false }));

  const list = element("joystick-list");
  list.replaceChildren();

  names.forEach((name, index) => {
    const row = document.createElement("div");
    row.className = "joystick";

    const title = document.createElement("div");
    title.className = "joystick-name";
    title.textContent = name;

    const where = document.createElement("div");
    where.className = "joystick-readout";
    where.textContent = wiring[index] ?? "";

    const pad = document.createElement("div");
    pad.className = "joystick-pad";
    const rest = document.createElement("div");
    rest.className = "joystick-rest";
    const knob = document.createElement("div");
    knob.className = "joystick-knob";
    pad.append(rest, knob);

    const readout = document.createElement("div");
    readout.className = "joystick-readout";

    // The cap switch, worked exactly as a tactile switch is: held rather than
    // toggled, and it captures the pointer so a press that wanders off the
    // button still ends on it.
    const press = document.createElement("button");
    press.type = "button";
    press.className = "switch-button";
    press.textContent = "Press (SW)";

    // The pad's radius comes off the element rather than the stylesheet: it is
    // sized as a fraction of the panel, so it is only known once it is laid
    // out -- and a pointer event is exactly when it has been.
    const deflectionAt = (event: PointerEvent): [number, number] => {
      const box = pad.getBoundingClientRect();
      const travel = Math.max(box.width / 2 - KNOB_RADIUS, 1);
      return gate(
        (event.clientX - (box.left + box.width / 2)) / travel,
        // Up the screen is up the stick: the page's y grows downwards and the
        // axis does not.
        -(event.clientY - (box.top + box.height / 2)) / travel,
      );
    };

    const draw = () => {
      const stick = sticks[index];
      const box = pad.getBoundingClientRect();
      const travel = Math.max(box.width / 2 - KNOB_RADIUS, 1);
      knob.style.left = `calc(50% + ${(stick.x * travel).toFixed(1)}px)`;
      knob.style.top = `calc(50% + ${(-stick.y * travel).toFixed(1)}px)`;
      readout.textContent = `x ${stick.x.toFixed(2)}   y ${stick.y.toFixed(2)}`;
    };

    const move = (x: number, y: number) => {
      const stick = sticks[index];
      stick.x = x;
      stick.y = y;
      draw();
      send(index);
    };

    pad.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      pad.setPointerCapture(event.pointerId);
      pad.classList.add("held");
      move(...deflectionAt(event));
    });
    pad.addEventListener("pointermove", (event) => {
      if (!pad.hasPointerCapture(event.pointerId)) {
        return;
      }
      move(...deflectionAt(event));
    });
    // Letting go is the springs: the gimbal returns to its centre, and it does
    // so whether the pointer was released over the pad or somewhere else.
    const release = () => {
      pad.classList.remove("held");
      move(0, 0);
    };
    pad.addEventListener("pointerup", release);
    pad.addEventListener("pointercancel", release);

    const cap = (pressed: boolean) => {
      press.classList.toggle("held", pressed);
      sticks[index].pressed = pressed;
      send(index);
    };
    press.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      press.setPointerCapture(event.pointerId);
      cap(true);
    });
    press.addEventListener("pointerup", () => cap(false));
    press.addEventListener("pointercancel", () => cap(false));

    row.append(title, where, pad, readout, press);
    list.append(row);
    draw();
  });

  element("joysticks").hidden = names.length === 0;
}

// Centre every stick on the board being watched and let go of every cap, before
// pointing the panel at another robot.
function releaseAll() {
  if (!hybrid) {
    return;
  }
  sticks.forEach((stick, index) => {
    if (stick.x === 0 && stick.y === 0 && !stick.pressed) {
      return;
    }
    stick.x = 0;
    stick.y = 0;
    stick.pressed = false;
    send(index);
  });
}

function send(index: number) {
  if (!hybrid) {
    return;
  }
  const stick = sticks[index];
  try {
    hybrid.set_joystick_at(robot, index, stick.x, stick.y, stick.pressed);
  } catch (error) {
    console.error("joysticks: could not move", error);
  }
}
